import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Row,
  Col,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import notify, { MESSAGES } from '../../../utils/notify';
import classnames from 'classnames';
import { getAuthToken, isAuthenticated } from '../../../auth/tokenvalidation';
import { formatDateToDDMMYYYY } from '../../../utils/dateformatter';
import ActionButton from '../../../Components/Common/ActionButton';
import { EmptyState } from '../../../Components/Common/ListStates';
import { usePermission } from '../../../contexts/PermissionContext';
import {
  ActiveFilterChips,
  ClearFiltersButton,
  ListFilterSelect,
  ListSearch,
} from '../../../Components/Common/ListFilters';
import { fetchJson } from '../../Procurement/utils/procurementList';
import { ProductAvatar } from '../../Procurement/component/ListBadges';
import '../../TenderOpening/components/TenderOpening.css';
import '../../Procurement/ProcurementView.css';
import {
  DEFAULT_SORT_FIELD,
  DEFAULT_STATUS_TAB,
  FREIGHT_PROCUREMENT_TYPE_MAP,
  FREIGHT_SORT_FIELDS,
  FREIGHT_STATUS_TABS,
  buildFreightListUrl,
  exportFreightList,
  isFreightAvailable,
  matchesFreightStatus,
} from '../utils/freightCostList';
import StatusBadge from '../../../Components/Common/StatusBadge';
import { getListProductTypeLabel } from '../../../shared/offer-domain/constants';

// `tabId` is a status tab id from FREIGHT_STATUS_TABS ('' = no status filter).
const STAT_CARDS = [
  {
    key: 'total',
    tabId: '',
    label: 'Total Freight Cost',
    icon: 'ri-stack-line',
    tone: 'total',
  },
  {
    key: 'notAvailable',
    tabId: 'notAvailable',
    label: 'Not Available',
    icon: 'ri-close-circle-line',
    tone: 'progress',
  },
  {
    key: 'available',
    tabId: 'available',
    label: 'Available',
    icon: 'ri-checkbox-circle-line',
    tone: 'done',
  },
];

const StatusPill = ({ row }) =>
  isFreightAvailable(row) ? (
    <StatusBadge tone='success' label={row.freightCostStatus} />
  ) : (
    <StatusBadge
      tone='warning'
      label={row?.freightCostStatus || 'Not Available'}
    />
  );

const ProductInfo = ({ item }) => {
  const name = item?.productType?.name;
  const sub = getListProductTypeLabel(item, item?.productType?.description);
  if (!name && !sub) return null;

  return (
    <div className='to-product'>
      <span className='to-product-name'>{name || '-'}</span>
      {sub && <span className='text-truncate ms-2'>· {sub}</span>}
    </div>
  );
};

const StatCard = ({ card, summary, isActive, onSelect }) => {
  const total = summary?.total || 0;
  const count = summary?.[card.key];
  const share =
    card.key !== 'total' && total > 0 && typeof count === 'number'
      ? Math.round((count / total) * 100)
      : null;

  return (
    <button
      type='button'
      className={classnames('to-stat', `to-stat--${card.tone}`, {
        'is-active': isActive,
      })}
      aria-pressed={isActive}
      onClick={onSelect}
    >
      <span className='to-stat-icon'>
        <i className={card.icon} aria-hidden='true' />
      </span>
      <span>
        <span className='to-stat-label'>{card.label}</span>
        <span className='to-stat-value'>
          {typeof count === 'number' ? count : '–'}
        </span>
      </span>
      {share !== null && <span className='to-stat-share'>{share}%</span>}
    </button>
  );
};

const SkeletonRows = ({ count = 5 }) => (
  <div className='w-100 px-3 py-2'>
    {Array.from({ length: count }).map((_, index) => (
      <div key={index} className='d-flex align-items-center gap-3 py-3'>
        <div className='to-skeleton' style={{ width: '9%' }} />
        <div className='to-skeleton' style={{ width: '26%' }} />
        <div className='to-skeleton' style={{ width: '12%' }} />
        <div className='to-skeleton' style={{ width: '18%' }} />
        <div className='to-skeleton flex-grow-1' />
      </div>
    ))}
  </div>
);

// The list endpoint is not split by procurement type, so the
// `procurementTypeHeader` passed by the tab wrappers is ignored here.
const FreightCostTable = ({ refreshTrigger = 0, exportName = 'List' }) => {
  const navigate = useNavigate();
  const { can } = usePermission();
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [statusTab, setStatusTab] = useState(DEFAULT_STATUS_TAB);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortField, setSortField] = useState(DEFAULT_SORT_FIELD);
  const [sortDirection, setSortDirection] = useState('desc');
  const [rawItems, setRawItems] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  // The list endpoint has no status filter and paginates server-side, so the
  // whole (search + sort) result set is fetched once; status filtering,
  // summary counts and pagination then run on the client.
  useEffect(() => {
    const token = getAuthToken();
    if (!token || !isAuthenticated()) {
      notify.error(MESSAGES.authRequired);
      return undefined;
    }

    const controller = new AbortController();
    setIsLoading(true);

    const query = {
      currentPage: 1,
      search: debouncedSearchText,
      sortField,
      sortDirection,
    };

    (async () => {
      let result = await fetchJson(
        buildFreightListUrl({ ...query, rowsPerPage: 10 }),
        token,
        controller.signal
      );
      const total = result?.data?.totalItems || 0;
      if (total > (result?.data?.items?.length || 0)) {
        result = await fetchJson(
          buildFreightListUrl({ ...query, rowsPerPage: total }),
          token,
          controller.signal
        );
      }
      return result?.data?.items || [];
    })()
      .then(rows => {
        setRawItems(rows);
      })
      .catch(error => {
        if (error.name === 'AbortError') return;
        notify.apiError(error, 'load procurement data');
        setRawItems([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [debouncedSearchText, sortField, sortDirection, refreshTrigger]);

  const handleStatusFilter = tabId => {
    setStatusTab(tabId);
    setCurrentPage(1);
  };

  const handleSort = useCallback((column, direction) => {
    setSortField(
      FREIGHT_SORT_FIELDS[column.id] || column.id || 'procurementnumber'
    );
    setSortDirection(direction);
    setCurrentPage(1);
  }, []);

  const handlePerRowsChange = newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchText('');
    setStatusTab(DEFAULT_STATUS_TAB);
    setCurrentPage(1);
  };

  // Status filtering, summary counts and pagination run on the client over
  // the full (search + sort) result set kept in `rawItems`.
  const filteredItems = rawItems.filter(row =>
    matchesFreightStatus(row, statusTab)
  );
  const totalRows = filteredItems.length;
  const summary = {
    total: rawItems.length,
    notAvailable: rawItems.filter(row => !isFreightAvailable(row)).length,
    available: rawItems.filter(isFreightAvailable).length,
  };
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const pagedItems = filteredItems.slice(
    (safePage - 1) * rowsPerPage,
    safePage * rowsPerPage
  );
  const statusLabel =
    FREIGHT_STATUS_TABS.find(tab => tab.id === statusTab)?.label ||
    'All Status';
  const filterChips = [
    searchText && {
      key: 'search',
      label: 'Search',
      value: searchText,
      onRemove: () => setSearchText(''),
    },
    statusTab !== DEFAULT_STATUS_TAB && {
      key: 'status',
      label: 'Status',
      value: statusLabel,
      onRemove: () => handleStatusFilter(DEFAULT_STATUS_TAB),
    },
  ].filter(Boolean);
  const hasFilters = filterChips.length > 0;
  const startEntry = totalRows === 0 ? 0 : (safePage - 1) * rowsPerPage + 1;
  const endEntry = Math.min(safePage * rowsPerPage, totalRows);
  const sortColumnId =
    Object.keys(FREIGHT_SORT_FIELDS).find(
      id => FREIGHT_SORT_FIELDS[id] === sortField
    ) || sortField;

  // Exports every row matching the current search + status filter. The full
  // result set is already in memory, so no extra fetch is needed.
  const handleExport = format => {
    if (!filteredItems.length) {
      notify.info(MESSAGES.noDataToExport);
      return;
    }
    exportFreightList(filteredItems, { format, sheetName: exportName });
  };

  const goToDetail = row => navigate(`/apps-freightcost/upd/${row.id}`);

  const emptyState = (
    <EmptyState
      title='No freight cost found'
      emptyHint='Freight cost requests appear here after a freight availability request is sent from a procurement in Tender Opening.'
      filteredHint='Nothing matches the current search or status filter.'
      hasFilters={hasFilters}
      onClear={clearFilters}
      secondaryAction={
        can('canRead', '/apps-tenderopening/view')
          ? {
              label: 'Go to Tender Opening',
              icon: 'ri-arrow-right-line',
              onClick: () => navigate('/apps-tenderopening/view'),
            }
          : undefined
      }
    />
  );

  const columns = [
    {
      id: 'procurementnumber',
      name: 'ID Proc',
      selector: row => row.procurementNumber,
      cell: row => (
        <span className='to-id'>{row.procurementNumber || '-'}</span>
      ),
      width: '10%',
      sortable: true,
    },
    {
      id: 'freightRequestDate',
      name: 'Requested Date',
      selector: row => row.freightRequestDate,
      cell: row => formatDateToDDMMYYYY(row.freightRequestDate) || '-',
      width: '10%',
      sortable: true,
    },
    {
      id: 'hpsnumber',
      name: 'Freight Cost No',
      selector: row => row.freightRequestNumber,
      cell: row => (
        <span className='to-id'>{row.freightRequestNumber || '-'}</span>
      ),
      width: '12%',
      sortable: true,
    },
    {
      id: 'titleprocurement',
      name: 'Procurement',
      selector: row => row.titleProcurement,
      cell: row => (
        <div className='d-flex align-items-center gap-2 py-2 w-100'>
          <ProductAvatar name={row.productType?.name} />
          <div className='pv-text-col flex-grow-1'>
            <div className='to-title' title={row.titleProcurement}>
              {row.titleProcurement || '-'}
            </div>
            <ProductInfo item={row} />
          </div>
        </div>
      ),
      width: '24%',
      sortable: true,
    },
    {
      id: 'procurementType',
      name: 'Proc Type',
      cell: row => (
        <span className='to-tag'>
          {FREIGHT_PROCUREMENT_TYPE_MAP[row.procurementType] || '-'}
        </span>
      ),
      width: '13%',
    },
    {
      id: 'description',
      name: 'Description',
      selector: row => row.description,
      cell: row => (
        <span className='pv-desc' title={row.description || ''}>
          {row.description || '-'}
        </span>
      ),
      width: '15%',
      sortable: true,
    },
    {
      id: 'statusHPS2',
      name: 'Status',
      selector: row => row.freightCostStatus,
      cell: row => <StatusPill row={row} />,
      width: '10%',
      sortable: true,
    },
    {
      name: <span className='text-center w-100'>Action</span>,
      cell: row => (
        <div className='d-flex gap-1 justify-content-center w-100'>
          <ActionButton
            variant='icon'
            size='sm'
            icon='ri-arrow-right-up-line'
            label={
              row.procurementNumber
                ? `Open detail ${row.procurementNumber}`
                : 'Open detail'
            }
            onClick={e => {
              e.stopPropagation();
              goToDetail(row);
            }}
          />
        </div>
      ),
      width: '6%',
      center: true,
    },
  ];

  const MobileCard = ({ item }) => (
    <div
      className={classnames(
        'to-mcard d-md-none',
        isFreightAvailable(item) ? 'to-mcard--success' : 'to-mcard--warning'
      )}
    >
      <div className='d-flex justify-content-between align-items-center gap-2 mb-2'>
        <span className='to-id'>{item.procurementNumber || '-'}</span>
        <StatusPill row={item} />
      </div>

      <div className='d-flex align-items-center gap-2'>
        <ProductAvatar name={item.productType?.name} />
        <div className='pv-text-col flex-grow-1'>
          <div className='to-title'>{item.titleProcurement || '-'}</div>
          <ProductInfo item={item} />
        </div>
      </div>

      <div className='to-mcard-meta'>
        <div>
          <span>Freight Cost No</span>
          <span>{item.freightRequestNumber || '-'}</span>
        </div>
        <div>
          <span>Requested</span>
          <span>{formatDateToDDMMYYYY(item.freightRequestDate) || '-'}</span>
        </div>
        <div>
          <span>Proc Type</span>
          <span>
            {FREIGHT_PROCUREMENT_TYPE_MAP[item.procurementType] || '-'}
          </span>
        </div>
      </div>

      {item.description && (
        <p className='pv-desc mb-3' title={item.description}>
          {item.description}
        </p>
      )}

      <div className='d-flex gap-2'>
        <ActionButton
          variant='secondary'
          size='sm'
          icon='ri-eye-line'
          className='flex-fill'
          onClick={() => goToDetail(item)}
        >
          View Detail
        </ActionButton>
      </div>
    </div>
  );

  return (
    <>
      <Row className='g-3 mb-3'>
        {STAT_CARDS.map(card => (
          <Col key={card.key} xs={12} sm={4}>
            <StatCard
              card={card}
              summary={summary}
              isActive={statusTab === card.tabId}
              onSelect={() => handleStatusFilter(card.tabId)}
            />
          </Col>
        ))}
      </Row>

      <div className='to-panel'>
        <div className='to-toolbar'>
          <ListSearch
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            placeholder='Search by title or freight cost no...'
            ariaLabel='Search freight cost'
          />
          <ListFilterSelect
            label='Status'
            value={statusTab}
            onChange={handleStatusFilter}
            allLabel='All Status'
            options={FREIGHT_STATUS_TABS.map(tab => ({
              value: tab.id,
              label: tab.label,
            }))}
          />
          <ClearFiltersButton
            count={filterChips.length}
            onClick={clearFilters}
          />
          <span className='to-toolbar-meta'>
            {isLoading
              ? 'Loading...'
              : `Showing ${startEntry}–${endEntry} of ${totalRows} freight costs`}
          </span>
          <UncontrolledDropdown>
            <DropdownToggle
              tag='button'
              type='button'
              className='btn btn-light btn-sm d-inline-flex align-items-center'
              disabled={isLoading || !totalRows}
            >
              <i className='ri-download-2-line me-2' aria-hidden='true' />
              Export
            </DropdownToggle>
            <DropdownMenu end>
              <DropdownItem onClick={() => handleExport('xlsx')}>
                <i className='ri-file-excel-2-line align-middle me-2 text-success'></i>
                Export to Excel
              </DropdownItem>
              <DropdownItem onClick={() => handleExport('csv')}>
                <i className='ri-file-text-line align-middle me-2 text-muted'></i>
                Export to CSV
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>

        <ActiveFilterChips chips={filterChips} />

        <div className='d-md-none p-3'>
          {isLoading && <SkeletonRows count={3} />}
          {!isLoading &&
            pagedItems.map((item, index) => (
              <MobileCard key={item.id || index} item={item} />
            ))}
          {!isLoading && !pagedItems.length && emptyState}
        </div>

        <div className='d-none d-md-block to-table-wrap'>
          <DataTable
            key={`sort-${sortColumnId}-${sortDirection}`}
            columns={columns}
            data={pagedItems}
            pagination={false}
            onSort={handleSort}
            sortServer
            defaultSortFieldId={sortColumnId}
            defaultSortAsc={sortDirection === 'asc'}
            onRowClicked={goToDetail}
            highlightOnHover
            pointerOnHover
            persistTableHead
            progressPending={isLoading}
            progressComponent={<SkeletonRows />}
            noDataComponent={emptyState}
            customStyles={{
              headCells: {
                style: { paddingLeft: '12px', paddingRight: '12px' },
              },
              cells: {
                style: {
                  paddingLeft: '12px',
                  paddingRight: '12px',
                  wordBreak: 'break-word',
                },
              },
              table: { style: { width: '100%' } },
              tableWrapper: { style: { display: 'table' } },
            }}
          />
        </div>

        <div className='to-footer'>
          <div className='d-flex align-items-center gap-2'>
            <span>Rows per page</span>
            <select
              className='form-select form-select-sm'
              style={{ width: 'auto' }}
              value={rowsPerPage}
              onChange={e => handlePerRowsChange(parseInt(e.target.value, 10))}
              aria-label='Rows per page'
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className='to-footer-range'>
              {startEntry}–{endEntry} of <strong>{totalRows}</strong> items
            </span>
          </div>

          <div className='to-pager'>
            <button
              type='button'
              className='to-pager-btn'
              disabled={safePage <= 1 || isLoading}
              onClick={() => setCurrentPage(1)}
              aria-label='First page'
              title='First page'
            >
              <i className='ri-skip-back-line' aria-hidden='true' />
            </button>
            <button
              type='button'
              className='to-pager-btn'
              disabled={safePage <= 1 || isLoading}
              onClick={() => setCurrentPage(safePage - 1)}
              aria-label='Previous page'
              title='Previous page'
            >
              <i className='ri-arrow-left-s-line' aria-hidden='true' />
            </button>
            <span className='to-pager-current'>
              Page {safePage} of {totalPages}
            </span>
            <button
              type='button'
              className='to-pager-btn'
              disabled={safePage >= totalPages || isLoading}
              onClick={() => setCurrentPage(safePage + 1)}
              aria-label='Next page'
              title='Next page'
            >
              <i className='ri-arrow-right-s-line' aria-hidden='true' />
            </button>
            <button
              type='button'
              className='to-pager-btn'
              disabled={safePage >= totalPages || isLoading}
              onClick={() => setCurrentPage(totalPages)}
              aria-label='Last page'
              title='Last page'
            >
              <i className='ri-skip-forward-line' aria-hidden='true' />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default React.memo(FreightCostTable);
